import React, { useEffect } from 'react';

import {
  ApartmentOutlined,
  CloudOutlined,
  GlobalOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  LockOutlined,
} from '@ant-design/icons';
import {
  Card,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';

import ShelfLocationTemplateInput from '@components/shared/shelves/ShelfLocationTemplateInput';
import { getCountryOptions, TWZIP } from '@constants/index';

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Taipei', label: '(GMT+08:00) Taipei' },
  { value: 'Asia/Tokyo', label: '(GMT+09:00) Tokyo' },
  { value: 'Asia/Seoul', label: '(GMT+09:00) Seoul' },
  { value: 'Asia/Shanghai', label: '(GMT+08:00) Shanghai' },
  { value: 'Asia/Hong_Kong', label: '(GMT+08:00) Hong Kong' },
  { value: 'Asia/Singapore', label: '(GMT+08:00) Singapore' },
  { value: 'Asia/Bangkok', label: '(GMT+07:00) Bangkok' },
  { value: 'Asia/Jakarta', label: '(GMT+07:00) Jakarta' },
  { value: 'Asia/Kuala_Lumpur', label: '(GMT+08:00) Kuala Lumpur' },
  { value: 'Asia/Manila', label: '(GMT+08:00) Manila' },
  { value: 'America/Los_Angeles', label: '(GMT-08:00) Los Angeles' },
  { value: 'America/New_York', label: '(GMT-05:00) New York' },
  { value: 'America/Chicago', label: '(GMT-06:00) Chicago' },
  { value: 'Europe/London', label: '(GMT+00:00) London' },
  { value: 'Europe/Paris', label: '(GMT+01:00) Paris' },
  { value: 'Europe/Berlin', label: '(GMT+01:00) Berlin' },
  { value: 'Australia/Sydney', label: '(GMT+11:00) Sydney' },
  { value: 'Pacific/Auckland', label: '(GMT+13:00) Auckland' },
];

const WarehouseForm = ({
  form,
  lockStorageMode = false,
  initialValues,
  locationTemplate,
  onLocationTemplateChange,
  onFinish,
}) => {
  const { t, i18n } = useTranslation();
  const storageModeValue = Form.useWatch('storage_mode', form);
  // virtual / dedicated 的混放開關由後端強制為 true，UI 隱藏避免讓使用者誤以為可關
  const mixedTypesLocked = storageModeValue === 'virtual' || storageModeValue === 'dedicated';

  useEffect(() => {
    if (mixedTypesLocked) {
      form.setFieldValue('allow_mixed_inventory_types', true);
    }
  }, [mixedTypesLocked, form]);

  // 跟 FormReceiverInformation / ContactAddressEdit 相同：國家=TW（或空）→ city/district 走 TWZIP 下拉並自動帶 postal_code
  const countryOptions = getCountryOptions(i18n.language);
  const country = Form.useWatch('country_code', form);
  const city = Form.useWatch('city', form);
  const district = Form.useWatch('district', form);
  const isTaiwanOrEmpty = !country || country === '' || country === 'TW';

  useEffect(() => {
    if (isTaiwanOrEmpty && city && district) {
      const cityData = (TWZIP || {})[city];
      if (cityData && cityData[district]) {
        const autoPostalCode = cityData[district];
        if (autoPostalCode !== form.getFieldValue('postal_code')) {
          form.setFieldsValue({ postal_code: autoPostalCode });
        }
      }
    }
  }, [city, district, isTaiwanOrEmpty, form]);

  const cityOptions = Object.keys(TWZIP || {}).map((cityName) => ({ value: cityName, label: cityName }));
  const districtOptions = (() => {
    const zipMap = TWZIP || {};
    if (!city || !zipMap[city]) return [];
    return Object.keys(zipMap[city]).map((d) => ({ value: d, label: d }));
  })();
  const handleCityChange = (value) => {
    form.setFieldsValue({ city: value, district: '', postal_code: '' });
  };

  const handleFinish = (values) => {
    onFinish({
      ...values,
      default_shelf_location_template: locationTemplate || null,
    });
  };

  const renderModeOption = (modeValue, Icon, descriptionKey) => (
    <Radio value={modeValue}>
      <Space>
        <Icon />
        <span>{t(`operator.organization_settings.storage_mode_${modeValue}`)}</span>
      </Space>
      <Typography.Text type="secondary" style={{ display: 'block', marginLeft: 22, fontSize: 12 }}>
        {t(`operator.organization_settings.storage_mode_${descriptionKey || modeValue}_description`)}
      </Typography.Text>
    </Radio>
  );

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={initialValues}
    >
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item
            name="name"
            label={t('models.warehouse.name')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="phone" label={t('models.warehouse.phone')}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={12}>
          <Form.Item name="country_code" label={t('models.warehouse.country_code', { defaultValue: '國家' })}>
            <Select
              placeholder={t('common.select.please_select')}
              allowClear
              showSearch
              optionFilterProp="label"
              options={countryOptions}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="state" label={t('models.warehouse.state', { defaultValue: '州 / 省' })}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={8}>
          <Form.Item
            name="city"
            label={t('models.warehouse.city', { defaultValue: '城市' })}
            getValueProps={(val) => ({ value: isTaiwanOrEmpty ? (val ? [val] : []) : val })}
            normalize={(val) => (Array.isArray(val) ? val[val.length - 1] || '' : val)}
          >
            {isTaiwanOrEmpty ? (
              <Select
                mode="tags"
                maxTagCount={1}
                placeholder={t('common.select.please_select')}
                allowClear
                showSearch
                optionFilterProp="label"
                options={cityOptions}
                tokenSeparators={[]}
                onChange={(values) => {
                  const v = Array.isArray(values) ? values[values.length - 1] || '' : values;
                  handleCityChange(v);
                }}
              />
            ) : (
              <Input />
            )}
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="district"
            label={t('models.warehouse.district', { defaultValue: '區域' })}
            getValueProps={(val) => ({ value: isTaiwanOrEmpty ? (val ? [val] : []) : val })}
            normalize={(val) => (Array.isArray(val) ? val[val.length - 1] || '' : val)}
          >
            {isTaiwanOrEmpty ? (
              <Select
                mode="tags"
                maxTagCount={1}
                placeholder={t('common.select.please_select')}
                allowClear
                showSearch
                optionFilterProp="label"
                options={districtOptions}
                tokenSeparators={[]}
              />
            ) : (
              <Input />
            )}
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="postal_code" label={t('models.warehouse.postal_code')}>
            <Input
              disabled={isTaiwanOrEmpty && !!city && !!district}
              placeholder={isTaiwanOrEmpty && city && district ? t('common.auto_filled') : ''}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={24}>
          <Form.Item name="address" label={t('models.warehouse.address')}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        {mixedTypesLocked ? (
          <Form.Item name="allow_mixed_inventory_types" hidden>
            <Switch />
          </Form.Item>
        ) : (
          <Col span={12}>
            <Form.Item
              name="allow_mixed_inventory_types"
              valuePropName="checked"
              label={
                <Space>
                  {t('models.warehouse.allow_mixed_inventory_types')}
                  <Tooltip title={t('models.warehouse.allow_mixed_inventory_types_tooltip')}>
                    <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                  </Tooltip>
                </Space>
              }
            >
              <Switch />
            </Form.Item>
          </Col>
        )}
        <Col span={mixedTypesLocked ? 24 : 12}>
          <Form.Item
            name="timezone"
            label={
              <Space>
                <GlobalOutlined />
                {t('models.warehouse.timezone')}
              </Space>
            }
          >
            <Select
              placeholder={t('models.warehouse.timezone_placeholder')}
              allowClear
              showSearch
              optionFilterProp="label"
              options={TIMEZONE_OPTIONS}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={24}>
          <Card
            size="small"
            title={t('operator.organization_settings.storage_mode')}
            style={{ marginBottom: 24, borderColor: '#d9d9d9', background: '#fafafa' }}
          >
            {lockStorageMode ? (
              <>
                <Form.Item name="storage_mode" noStyle>
                  <Input type="hidden" />
                </Form.Item>
                <Space>
                  <LockOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                  <Typography.Text strong>
                    {storageModeValue
                      ? t(`operator.organization_settings.storage_mode_${storageModeValue}`)
                      : ''}
                  </Typography.Text>
                </Space>
                <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                  {t('operator.organization_settings.storage_mode_locked_notice')}
                </Typography.Paragraph>
              </>
            ) : (
              <Form.Item name="storage_mode" noStyle>
                <Radio.Group>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {renderModeOption('dynamic', ApartmentOutlined, 'dynamic')}
                    {renderModeOption('dedicated', HomeOutlined, 'dedicated')}
                    {renderModeOption('virtual', CloudOutlined, 'virtual')}
                  </Space>
                </Radio.Group>
              </Form.Item>
            )}
          </Card>
        </Col>
      </Row>

      {storageModeValue !== 'virtual' && (
        <Row gutter={24}>
          <Col span={24}>
            <Card
              size="small"
              title={t('models.warehouse.default_shelf_location_template')}
              style={{ marginBottom: 24, borderColor: '#d9d9d9', background: '#fafafa' }}
            >
              <ShelfLocationTemplateInput
                value={locationTemplate}
                onChange={onLocationTemplateChange}
                showPreview={true}
              />
            </Card>
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default WarehouseForm;
